/**
 * GroupConfigPanel Component for UI-Model-1
 *
 * Configuration panel for editing group properties.
 * Shows when a group is selected in the NavigationPreview.
 */

import React, { useCallback } from 'react';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { createUpdateGroupCommand } from '../../types/command.types';
import { GroupAPI } from '../../types/api.types';

interface GroupConfigPanelProps {
  id?: string;
  groupId: string;
}

export const GroupConfigPanel = ({ id, groupId }: GroupConfigPanelProps) => {
  const baseId = id || 'group-config-panel';
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const navigation = state.model?.layout.navigation;

  // Find the group
  const groups = state.model?.layout.navigation.groups || [];
  const group = groups.find((g) => g.id === groupId);

  // Helper to dispatch group updates
  const updateGroup = useCallback(
    (updates: Partial<Pick<GroupAPI, 'name' | 'orientation' | 'configuration'>>, description?: string) => {
      const command = createUpdateGroupCommand(
        groupId,
        updates,
        description || 'Update group configuration'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      if (navigation) {
        const updatedGroups = navigation.groups.map((g) => {
          if (g.id !== groupId) return g;
          let updatedGroup = {
            ...g,
            ...updates,
            configuration: updates.configuration
              ? { ...g.configuration, ...updates.configuration }
              : g.configuration,
          };
          if (updates.name && updatedGroup.configuration) {
            updatedGroup = {
              ...updatedGroup,
              configuration: { ...updatedGroup.configuration, name: updates.name },
            };
          }
          return updatedGroup;
        });
        enqueueSave('navigation', { navigationData: { ...navigation, groups: updatedGroups } });
      }
    },
    [dispatch, groupId, navigation, enqueueSave]
  );

  // Debounced input for group name
  const groupNameInput = useDebouncedInput(
    group?.name || '',
    (value) => updateGroup({ name: value }, 'Update group name'),
    500
  );

  if (!group) {
    return (
      <div id={baseId} data-testid={baseId} className="tw-p-4 tw-text-ink-secondary tw-text-sm">
        Group not found
      </div>
    );
  }

  const showTitle = group.configuration?.showTitle !== false;

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      {/* Section: Group Settings */}
      <div className="tw-space-y-4">
        <FieldTitle label="Group Settings" size="sm" />

        <TextField
          label="Group Name"
          labelSize="sm"
          value={groupNameInput.localValue}
          onChange={groupNameInput.handleChange}
          placeholder="Enter group name"
        />

        <Toggle
          label="Show Title"
          checked={showTitle}
          onChange={(checked) =>
            updateGroup(
              { configuration: { ...group.configuration, showTitle: checked } },
              `${checked ? 'Show' : 'Hide'} group title`
            )
          }
        />
      </div>
    </div>
  );
};

export default GroupConfigPanel;
